import { GetMemberProgramQuery } from '@src/graphqlTypes/catalogServiceTypes';
import React from 'react';
import Helmet from 'react-helmet';

const renderTitle = (title?: string) =>
	title ? `${title} | Program Catalog | Guild` : 'Program Catalog | Guild';
const renderURL = path => `${window.location.origin}${path || ''}`;
const getMetaTags = ({ title, description, path, contentType, image, robots }) => {
	const metaTags = [
		{ name: 'title', content: renderTitle(title) },
		{ name: 'og:title', content: renderTitle(title) },
		{ name: 'type', content: contentType },
		{ name: 'og:type', content: contentType },
		{ name: 'url', content: renderURL(path) },
		{ name: 'og:url', content: renderURL(path) },
		{ name: 'image', content: image },
		{ name: 'og:image', content: image },
		{ name: 'description', content: description },
		{ name: 'og:description', content: description },
		{ name: 'site_name', content: 'Guild' },
		{ name: 'og:site_name', content: 'Guild' },
		{ name: 'msapplication-TileColor', content: '#ffffff' },
		{
			name: 'msapplication-TileImage',
			content: 'https://recess-images.guildeducation.com/icons/favicon.png?w=144',
		},
		{ name: 'theme-color', content: '#ffffff' },
		{ name: 'robots', content: robots },
	];

	return metaTags;
};

const getLinkTags = path => {
	const links = [
		{ rel: 'canonical', href: renderURL(path) },
		{
			rel: 'apple-touch-icon',
			sizes: '57x57',
			href: 'https://recess-images.guildeducation.com/icons/favicon.png?w=57',
		},
		{
			rel: 'apple-touch-icon',
			sizes: '60x60',
			href: 'https://recess-images.guildeducation.com/icons/favicon.png?w=60',
		},
		{
			rel: 'apple-touch-icon',
			sizes: '72x72',
			href: 'https://recess-images.guildeducation.com/icons/favicon.png?w=72',
		},
		{
			rel: 'apple-touch-icon',
			sizes: '76x76',
			href: 'https://recess-images.guildeducation.com/icons/favicon.png?w=76',
		},
		{
			rel: 'apple-touch-icon',
			sizes: '114x114',
			href: 'https://recess-images.guildeducation.com/icons/favicon.png?w=114',
		},
		{
			rel: 'apple-touch-icon',
			sizes: '120x120',
			href: 'https://recess-images.guildeducation.com/icons/favicon.png?w=120',
		},
		{
			rel: 'apple-touch-icon',
			sizes: '144x144',
			href: 'https://recess-images.guildeducation.com/icons/favicon.png?w=144',
		},
		{
			rel: 'apple-touch-icon',
			sizes: '152x152',
			href: 'https://recess-images.guildeducation.com/icons/favicon.png?w=152',
		},
		{
			rel: 'apple-touch-icon',
			sizes: '180x180',
			href: 'https://recess-images.guildeducation.com/icons/favicon.png?w=180',
		},
		{
			rel: 'icon',
			type: 'image/png',
			sizes: '192x192',
			href: 'https://recess-images.guildeducation.com/icons/favicon.png?w=192',
		},
		{
			rel: 'icon',
			type: 'image/png',
			href: 'https://recess-images.guildeducation.com/icons/favicon.png?w=196',
			sizes: '196x196',
		},
		{
			rel: 'icon',
			type: 'image/png',
			href: 'https://recess-images.guildeducation.com/icons/favicon.png?w=96',
			sizes: '96x96',
		},
		{
			rel: 'icon',
			type: 'image/png',
			href: 'https://recess-images.guildeducation.com/icons/favicon.png?w=32',
			sizes: '32x32',
		},
		{
			rel: 'icon',
			type: 'image/png',
			href: 'https://recess-images.guildeducation.com/icons/favicon.png?w=16',
			sizes: '16x16',
		},
		{
			rel: 'icon',
			type: 'image/png',
			href: 'https://recess-images.guildeducation.com/icons/favicon.png?w=128',
			sizes: '128x128',
		},
	];

	return links;
};

interface Props {
	title?: string;
	description?: string;
	path?: string;
	contentType?: string;
	schema?: string;
	offering?: GetMemberProgramQuery['memberProgram'];
}

export const SEO: React.FC<Props> = ({
	title,
	description,
	path,
	contentType,
	schema,
	offering,
}) => {
	const safeTitle = title || 'Explore Online Programs for Working Adults';
	const safeDescription =
		description || 'Guild helps working adults advance their careers through online education.';
	const safePath = path || '/';
	const safeImage = offering
		? offering.studentFacingAcademicPartner.logos.color
		: 'https://recess-images.guildeducation.com/guild_logos/guild/guild_color.svg';
	const safeContentType = contentType || 'website';
	const robots = offering && !offering.visible ? 'noindex,nofollow' : 'index,follow';

	return (
		<Helmet
			htmlAttributes={{
				itemscope: undefined,
				itemtype: `http://schema.org/${schema}`,
			}}
			link={getLinkTags(path)}
			meta={getMetaTags({
				title: safeTitle,
				description: safeDescription,
				path: safePath,
				contentType: safeContentType,
				image: safeImage,
				robots,
			})}
			title={renderTitle(title)}
		/>
	);
};
